import * as Sentry from '@sentry/vue'
import AIResponseAPI from '@/api/aiResponse'
import utils from '@/util'
import EventBus from '@/eventBus'
import PresentationUtils from '@/utils/presentation'
import SlideApi from '@/api/slide'
import { generateBlockHash, generateBlockId } from '@/utils/canvas-block-layouts/common'
import i18nPresenter from '@/i18nPresenter'
import AIResponseType from '@/constant/aiResponseType'
import textUtils from '@/utils/textUtil'
import eventCategories from '@/constant/eventCategories'
import router from '@/router'

// trackers
import SlideMixpanelTracker from '@/utils/mixpanel/slide.mixpanel-tracker'
import PresentationMixpanelTracker from '@/utils/mixpanel/presentation.mixpanel-tracker'

// HELPERS
const _setPayloadInsertAfter = (payload, rootState) => {
  const activeIndex = rootState.presentation.slides.findIndex((s) => {
    if (s.id) {
      return s.id === rootState.slideActive.id
    }
    return s.timestamp === rootState.slideActive.timestamp
  })
  let newSlideIndex = activeIndex
  if (activeIndex > -1 && !rootState.isNewPresentation) {
    const isNextSlideLeaderboard = rootState.presentation.slides[activeIndex + 1] && rootState.presentation.slides[activeIndex + 1].type === 'leaderboard'
    const activeSlideId = rootState.presentation.slides[activeIndex] &&
    rootState.presentation.slides[activeIndex].id

    if (!activeSlideId) {
      payload.insertAfter = rootState.presentation.slides[activeIndex - 1].order
    }
    if (activeSlideId && !isNextSlideLeaderboard) {
      payload.insertAfter = rootState.presentation.slides[activeIndex].order
      newSlideIndex = activeIndex + 1
    }
    if (activeSlideId && isNextSlideLeaderboard) {
      payload.insertAfter = rootState.presentation.slides[activeIndex].order + 1
      newSlideIndex = activeIndex + 2
    }
  }
  return [newSlideIndex, activeIndex]
}

const _setPayloadCreateGeneratedSlide = (aiResponseId, rootState, getters) => {
  const payload = {
    aiResponseId,
    presentationId: rootState.presentation.id,
    textColour: getters.defaultThemeValues?.textColour,
    baseColour: getters.defaultThemeValues?.baseColour,
    backgroundImage: getters.defaultThemeValues?.backgroundImage,
    visibility: getters.defaultThemeValues?.visibility,
  }

  // set payload.order for new slide
  const [newSlideIndex, activeIndex] = _setPayloadInsertAfter(payload, rootState)
  return [payload, newSlideIndex, activeIndex]
}

const backupBlocks = (oldData, newData, previousSelectingBlocks, rootState, addActionToHistoryFunction) => {
  addActionToHistoryFunction({
    type: 'UpdateBlocks',
    data: {
      canvasBlocks: oldData,
      selectingBlocks: previousSelectingBlocks,
      slideActiveId: rootState.slideActive.id,
    },
    newData: {
      canvasBlocks: newData,
      selectingBlocks: previousSelectingBlocks,
      slideActiveId: rootState.slideActive.id,
    },
    slideActiveId: rootState.slideActive.id,
  })
}

const _setNewSlideForContentSlide = (newSlide, rootState, updateCanvasBlocksFunction, addActionToHistoryFunction) => {
  const newCanvasBlocks = {}
  newSlide.canvasBlocks.forEach((block) => {
    block.id = generateBlockId(newSlide.id)
    block.h = generateBlockHash()
    newCanvasBlocks[block.id] = {
      id: block.id,
      type: 'RichText',
      tag: block.tag,
      content: block.content,
      style: block.style,
      h: block.h,
    }
  })
  newSlide.canvasBlocks = newCanvasBlocks
  updateCanvasBlocksFunction(newSlide.canvasBlocks, newSlide.id) // CanvasBlocksUpdateMixin
  backupBlocks(newSlide.canvasBlocks, newSlide.canvasBlocks, null, rootState, addActionToHistoryFunction)
}

const _makeSlideActiveAndSetSelectedSlideIds = (newSlide) => {
  EventBus.$emit('SideLeft: setSelectedSlideIds', [newSlide.id])
}

const _deleteDefaultPollSlide = (activeIndex, rootState) => {
  rootState.isNewPresentation = false
  const defaultSlide = rootState.presentation && rootState.presentation.slides &&
    rootState.presentation.slides[activeIndex]
  const defaultSlideId = defaultSlide && defaultSlide.id
  if (!defaultSlide || !defaultSlideId) return
  const isDefaultPollSlide = defaultSlide && defaultSlideId && defaultSlide.type === 'poll' && !defaultSlide.title
  if (!isDefaultPollSlide) return
  // Delete default slide: poll
  SlideApi.updateSlideById(defaultSlideId, {
    deleted: true,
  })
}

const _getSlideTitle = (newSlide, isFreestyleSlideWithCanvasBlocks) => {
  if (isFreestyleSlideWithCanvasBlocks) {
    return textUtils.getFirstCanvasBlockContent(newSlide.canvasBlocks)
  }
  return newSlide.title
}

const _handleSuggestPresentationTitle = (newSlide, isFreestyleSlideWithCanvasBlocks) => {
  const newSlideTitle = _getSlideTitle(newSlide, isFreestyleSlideWithCanvasBlocks)
  PresentationUtils.handleSuggestPresentationTitle(newSlideTitle)
}

const _updatePresentationSlides = async (dispatch, rootState, newSlideIndex, newSlide) => {
  const updatedSlides = [...rootState.presentation.slides]
  updatedSlides.splice(newSlideIndex, 0, newSlide)

  await dispatch('patchPresentationAction', { slides: updatedSlides }, { root: true })
}

const _handleAfterAddSlide = async (newSlide, newSlideIndex, activeIndex, rootState, dispatch,
  updateCanvasBlocksFunction, addActionToHistoryFunction) => {
  const isFreestyleSlideWithCanvasBlocks = newSlide.type === AIResponseType.FREESTYLE &&
    newSlide.canvasBlocks
  const isTypeAnswerSlide = newSlide.type === AIResponseType.PICK_ANSWER &&
    newSlide.slideType === AIResponseType.TYPE_ANSWER

  if (isFreestyleSlideWithCanvasBlocks) {
    _setNewSlideForContentSlide(newSlide, rootState, updateCanvasBlocksFunction, addActionToHistoryFunction)
  } else if (isTypeAnswerSlide) {
    newSlide.otherCorrectQuiz = JSON.parse(newSlide.otherCorrectQuiz)
  }

  await _updatePresentationSlides(dispatch, rootState, newSlideIndex, newSlide)

  dispatch('setSlideActiveAction', newSlide, { root: true })
  EventBus.$emit(`update-right-column-for-slide-title-${rootState.slideActive.id}`, newSlide.title)
  if (PresentationUtils.needSuggestPresentationTitle()) {
    _handleSuggestPresentationTitle(newSlide, isFreestyleSlideWithCanvasBlocks)
  }
  await utils.sleep(250)
  _makeSlideActiveAndSetSelectedSlideIds(newSlide)
  utils.showToastSuccess(i18nPresenter.t('Slide added successfully'))
  if (rootState.isNewPresentation) {
    _deleteDefaultPollSlide(activeIndex, rootState)
  }
  if (newSlide.type === AIResponseType.PICK_ANSWER) {
    EventBus.$emit('addSlideLeaderboard')
  }
}
const _setTrackingSlideTypeText = (type) => {
  let text = type
  if (type === AIResponseType.TYPE_ANSWER) {
    text = `${AIResponseType.PICK_ANSWER} ${AIResponseType.TYPE_ANSWER}`
  }
  return text
}

const trackGAWhenCreateNewSlide = (response, rootState) => {
  const trackingSlideType = _setTrackingSlideTypeText(response.type)
  const presentationTracker = new PresentationMixpanelTracker(rootState.presentation)
  const slideTracker = new SlideMixpanelTracker(rootState.presentation, eventCategories.PRESENTATION_EDITOR)
  slideTracker.setCustomProps({
    ...presentationTracker.getPresentationContext(),
    slideId: rootState.slideActive.id,
    slideType: trackingSlideType,
    touchPoint: 'AI Panel',
  })
  slideTracker.trackCreateNewSlide('AI-generated', trackingSlideType)
}

const trackDocToSlideCreateNewSlide = (rootState, slideType, slideId) => {
  const slideTracker = new SlideMixpanelTracker(rootState.presentation, router.currentRoute.name)
  slideTracker.setCustomProps({
    presentationId: rootState.presentation.id,
    userId: rootState.currentUser.id,
    slideType,
    slideId,
  })
  slideTracker.trackCreateNewSlide('From PPT/PDF to quiz', null, true) // useGA4Tracking = true
}

const setAISlideCountForTracking = (socketData, dispatch) => {
  const { aiGeneratedSlideCount, importedFileAIGeneratedSlideCount } = socketData

  if (!aiGeneratedSlideCount && !importedFileAIGeneratedSlideCount) return
  dispatch('updateCurrentUserAction', { hasUsedAIGeneratedSlides: true }, { root: true })
  dispatch('patchPresentationAction', { presentationContainsAIGeneratedSlides: true }, { root: true })

  if (aiGeneratedSlideCount) {
    dispatch('patchPresentationAction', { aiGeneratedSlideCount }, { root: true })
  }
  if (importedFileAIGeneratedSlideCount) {
    dispatch('patchPresentationAction', { importedFileAIGeneratedSlideCount }, { root: true })
  }
}

const _state = {

}

const _getters = {
  defaultThemeValues: (_, __, ___, rootGetters) => rootGetters['themeModule/defaultThemeValues'],
}

const actions = {
  async addSlideToPresentationAction({ dispatch, rootState, getters }, slidePayload) {
    const {
      response, updateCanvasBlocksFunction, addActionToHistoryFunction, type,
    } = slidePayload
    const isAddingSlideActionName = `${type}/setLoadingButtonAddSlideAction`
    try {
      if (response.isAddingSlide) return
      if (rootState.isReachLimitSlides) return
      dispatch(isAddingSlideActionName, { response, value: true }, { root: true })
      const [payload, newSlideIndex, activeIndex] = _setPayloadCreateGeneratedSlide(
        response.id,
        rootState,
        getters,
      )
      const res = await AIResponseAPI.createSlide(response.id, payload)
      _handleAfterAddSlide(res.body.newSlide, newSlideIndex, activeIndex,
        rootState, dispatch, updateCanvasBlocksFunction, addActionToHistoryFunction)
      setAISlideCountForTracking(res.body, dispatch)
      if (type === 'docToSlideGenerator') trackDocToSlideCreateNewSlide(rootState, response.type, res.body.newSlide.id)
      dispatch('aiConversation/setNumberOfSlidesAddedAction', null, { root: true })
    } catch (error) {
      const hasErrorMessage = error.body && error.body.errors && error.body.errors[0] && error.body.errors[0].message
      if (hasErrorMessage && error.body.errors[0].message === 'Presentation has reached slide limit number.') {
        rootState.isReachLimitSlides = true
        EventBus.$emit('openModalLimitSlides')
      }
      utils.showToastError(i18nPresenter.t('Cannot add this slide to current presentation. Please try again'))
      Sentry.captureException(error)
    } finally {
      dispatch(isAddingSlideActionName, { response, value: false }, { root: true })
      trackGAWhenCreateNewSlide(response, rootState)
    }
  },

}

const mutations = {}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
